import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Wrench, Upload, X, Loader2, Phone } from "lucide-react";

export const MaintenanceForm = ({
  t,
  isRTL,
  locationType,
  clinicMedicalDevices,
  clinicElectronicDevices = [],
  formData,
  setFormData,
  categories,
  images,
  handleImageUpload,
  removeImage,
  submitting,
  handleSubmit,
  onBack,
}) => {
  // Whether the current scan can offer a "Medical Device" dropdown (biomed inventory)
  const hasBiomedInventory =
    (locationType === "clinic" ||
      locationType === "facility" ||
      locationType === "office") &&
    clinicMedicalDevices.length > 0;
  const medicalDropdownActive =
    formData.element_type === "medical" && hasBiomedInventory;
  const itDropdownActive =
    formData.element_type === "it" && clinicElectronicDevices.length > 0;

  return (
  <Card className="border-0 shadow-sm">
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle className="text-lg text-[#4a3fa8]">{t.requestDetails}</CardTitle>
        {onBack && (
          <Button variant="ghost" size="sm" onClick={onBack} className="text-slate-500" data-testid="maintenance-back-btn">
            {isRTL ? "← رجوع" : "← Back"}
          </Button>
        )}
      </div>
    </CardHeader>
    <CardContent>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Reporter Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="block text-right">{t.name} *</Label>
            <Input
              value={formData.reporter_name}
              onChange={(e) => setFormData({ ...formData, reporter_name: e.target.value })}
              placeholder={t.namePlaceholder}
              className={isRTL ? "text-right" : "text-left"}
              required
              data-testid="reporter-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="block text-right">{t.whatsapp} *</Label>
            <div className="flex gap-2 items-center" dir="ltr">
              <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-md px-3 h-10 text-sm font-medium text-slate-700 shrink-0">
                <Phone className="w-3.5 h-3.5 text-green-500" />
                <span>966+</span>
              </div>
              <Input
                value={formData.reporter_whatsapp}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  const cleaned = val.startsWith("0") ? val.slice(1) : val;
                  if (cleaned.length <= 9) {
                    setFormData({ ...formData, reporter_whatsapp: cleaned });
                  }
                }}
                placeholder="5XXXXXXXX"
                className="text-left"
                dir="ltr"
                required
                data-testid="reporter-whatsapp-input"
              />
            </div>
            <p className={`text-xs text-slate-400 ${isRTL ? "text-right" : "text-left"}`}>
              {isRTL ? "أدخل الرقم بدون صفر في البداية" : "Enter number without leading zero"}
            </p>
          </div>
        </div>
        <p className={`text-xs text-slate-400 ${isRTL ? "text-right" : "text-left"}`}>
          {isRTL ? "سيتم إشعارك عند حل المشكلة" : "You will be notified when resolved"}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="block text-right">{t.elementType} *</Label>
            <Select
              value={formData.element_type}
              onValueChange={(v) => setFormData({ ...formData, element_type: v, category: "" })}
            >
              <SelectTrigger data-testid="public-element-type-select">
                <SelectValue placeholder={t.selectElementType} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="structural">{t.structural}</SelectItem>
                {locationType !== "restroom" && (
                  <>
                    {(locationType === "clinic" || hasBiomedInventory) && (
                      <SelectItem value="medical">{t.medical}</SelectItem>
                    )}
                    <SelectItem value="it">{t.it}</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="block text-right">
              {medicalDropdownActive
                ? isRTL
                  ? "الجهاز الطبي *"
                  : "Medical Device *"
                : itDropdownActive
                ? isRTL
                  ? "الجهاز الإلكتروني *"
                  : "Electronic Device *"
                : `${t.category} *`}
            </Label>
            {formData.element_type === "medical" &&
            (locationType === "clinic" || locationType === "facility" || locationType === "office") ? (
              clinicMedicalDevices.length > 0 ? (
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger data-testid="public-biomed-device-select">
                    <SelectValue placeholder={isRTL ? "اختر الجهاز" : "Select device"} />
                  </SelectTrigger>
                  <SelectContent>
                    {clinicMedicalDevices.map((d) => (
                      <SelectItem key={d.id} value={`biomed:${d.id}`}>
                        {d.biomed_no} — {d.device_name}
                        {d.manufacturer ? ` (${d.manufacturer})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 text-right">
                  {isRTL
                    ? "لا توجد أجهزة طبية مسجّلة لهذا الموقع. يرجى التواصل مع الأدمن لإدخالها أولاً."
                    : "No medical devices registered for this location yet. Please ask the admin to add them first."}
                </div>
              )
            ) : itDropdownActive ? (
              <Select
                value={formData.category}
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger data-testid="public-electronic-device-select">
                  <SelectValue placeholder={isRTL ? "اختر الجهاز" : "Select device"} />
                </SelectTrigger>
                <SelectContent>
                  {clinicElectronicDevices.map((d) => (
                    <SelectItem key={d.id} value={`biomed:${d.id}`}>
                      {d.biomed_no} — {d.device_name}
                      {d.manufacturer ? ` (${d.manufacturer})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Select
                value={formData.category}
                onValueChange={(v) => setFormData({ ...formData, category: v })}
                disabled={!formData.element_type}
              >
                <SelectTrigger data-testid="public-category-select">
                  <SelectValue placeholder={t.selectCategory} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="block text-right">{t.priority} *</Label>
          <Select
            value={formData.priority}
            onValueChange={(v) => setFormData({ ...formData, priority: v })}
          >
            <SelectTrigger data-testid="public-priority-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">{t.priorityLow}</SelectItem>
              <SelectItem value="medium">{t.priorityMedium}</SelectItem>
              <SelectItem value="high">{t.priorityHigh}</SelectItem>
              <SelectItem value="urgent">{t.priorityUrgent}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="block text-right">{t.description} *</Label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder={t.descriptionPlaceholder}
            className={`min-h-[120px] ${isRTL ? "text-right" : "text-left"}`}
            required
            data-testid="public-description-textarea"
          />
        </div>

        <div className="space-y-2">
          <Label className="block text-right">
            {t.addPhotos} <span className="text-red-500">*</span>
          </Label>
          <div
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
              images.length === 0
                ? "border-red-300 bg-red-50 hover:border-red-400"
                : "border-slate-200 hover:border-[#9b8fd4]"
            }`}
          >
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="hidden"
              id="public-image-upload"
              data-testid="public-image-upload-input"
            />
            <label htmlFor="public-image-upload" className="cursor-pointer">
              <Upload
                className={`w-10 h-10 mx-auto mb-3 ${
                  images.length === 0 ? "text-red-400" : "text-slate-400"
                }`}
              />
              <p className="text-slate-600 mb-1">{t.clickToUpload}</p>
              <p
                className={`text-sm ${
                  images.length === 0 ? "text-red-500 font-medium" : "text-slate-400"
                }`}
              >
                {t.maxPhotos}
              </p>
            </label>
          </div>

          {images.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-4">
              {images.map((img, index) => (
                <div key={index} className="relative">
                  <img
                    src={img}
                    alt={`Uploaded ${index + 1}`}
                    className="w-20 h-20 object-cover rounded-lg border"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Button
          type="submit"
          className="w-full bg-[#655dc6] hover:bg-[#5449a8] py-6 text-lg text-white"
          disabled={submitting}
          data-testid="public-submit-request-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mx-2 text-white" />
              <span className="text-white">{t.submitting}</span>
            </>
          ) : (
            <>
              <Wrench className="w-5 h-5 mx-2 text-white" />
              <span className="text-white">{t.submitRequest}</span>
            </>
          )}
        </Button>
      </form>
    </CardContent>
  </Card>
  );
};
